import React, { useState, useRef, useEffect } from 'react';
import useDashboardStore from '../store/dashboardStore';
import { useAuth } from '../contexts/AuthContext';
import NotificationCenter from './NotificationCenter';
import { isNativeApp } from '../config';

const Header = ({ onShowPricing, onLogout, onOpenSearch, isDemo = false }) => {
  const { user } = useAuth();
  const { fetchDashboardStats, loadDemoData } = useDashboardStore(
    (state) => ({
      fetchDashboardStats: state.fetchDashboardStats,
      loadDemoData: state.loadDemoData,
    })
  );
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef(null);

  const handleRefresh = isNativeApp || isDemo ? loadDemoData : fetchDashboardStats;

  // Close menu on outside click
  useEffect(() => {
    if (!showUserMenu) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showUserMenu]);

  const displayName = isDemo ? 'Demo User' : (user?.name || user?.email || 'Account');
  const initials = isDemo ? 'D' : (user?.name?.charAt(0) || user?.email?.charAt(0) || 'U').toUpperCase();
  const isMac = typeof navigator !== 'undefined' && navigator.platform?.includes('Mac');

  return (
    <header className="header" role="banner">
      <div className="welcome-text">
        <h1>QuantrolixAI Amazon FBA Control Center</h1>
        <p>Real-time arbitrage radar for Amazon, Walmart & eBay sellers</p>
      </div>
      <div className="header-actions" role="group" aria-label="Header actions">
        {/* Search trigger button */}
        <button
          onClick={onOpenSearch}
          aria-label="Search (Ctrl+K)"
          title={`Search (${isMac ? 'Cmd' : 'Ctrl'}+K)`}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'rgba(102, 126, 234, 0.1)',
            border: '1px solid rgba(102, 126, 234, 0.25)',
            borderRadius: '10px',
            padding: '8px 14px',
            color: 'rgba(255, 255, 255, 0.6)',
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontSize: '0.82rem',
            minWidth: '44px',
            minHeight: '44px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(102, 126, 234, 0.18)';
            e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.4)';
            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.8)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(102, 126, 234, 0.1)';
            e.currentTarget.style.borderColor = 'rgba(102, 126, 234, 0.25)';
            e.currentTarget.style.color = 'rgba(255, 255, 255, 0.6)';
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <span className="header-search-label">Search...</span>
          <span className="header-search-kbd" style={{
            marginLeft: 'auto',
            display: 'flex',
            gap: '3px',
          }}>
            <kbd style={{
              padding: '1px 5px',
              borderRadius: '4px',
              background: 'rgba(102, 126, 234, 0.12)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
              fontSize: '0.65rem',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: 500,
              lineHeight: 1.4,
            }}>
              {isMac ? 'Cmd' : 'Ctrl'}
            </kbd>
            <kbd style={{
              padding: '1px 5px',
              borderRadius: '4px',
              background: 'rgba(102, 126, 234, 0.12)',
              border: '1px solid rgba(102, 126, 234, 0.2)',
              fontSize: '0.65rem',
              fontFamily: 'Inter, system-ui, sans-serif',
              fontWeight: 500,
              lineHeight: 1.4,
            }}>
              K
            </kbd>
          </span>
        </button>

        {/* Pricing/upgrade CTA is web-only — Apple builds ship free (3.1.1),
            so App.jsx passes onShowPricing=undefined and the button is hidden. */}
        {onShowPricing && (
        <button
          className="quantum-btn"
          onClick={onShowPricing}
          aria-label={isDemo ? 'Create a free account' : 'View subscription plans'}
        >
          <span className="header-btn-text">
            {isDemo ? 'Create Free Account' : 'View Plans'}
          </span>
          {/* Icon-only fallback visible on mobile when text is hidden */}
          <span className="header-btn-icon" aria-hidden="true" style={{ display: 'none' }}>
            {isDemo ? '$' : '$'}
          </span>
        </button>
        )}
	      <button
	        className="quantum-btn"
	        onClick={handleRefresh}
	        aria-label={isNativeApp ? 'Refresh review data' : isDemo ? 'Reset demo data' : 'Sync live data from server'}
          style={{
            background: 'rgba(102, 126, 234, 0.2)',
            border: '1px solid #667eea'
          }}
        >
          <span className="header-btn-text">
            {isNativeApp ? 'Refresh Data' : isDemo ? 'Reset Demo Data' : 'Sync Live Data'}
          </span>
          <span className="header-btn-icon" aria-hidden="true" style={{ display: 'none' }}>
            {'\u21BB'}
          </span>
        </button>

        {/* Notifications */}
        <NotificationCenter />

        {/* User menu */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            aria-label="User menu"
            aria-expanded={showUserMenu}
            aria-haspopup="true"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(102, 126, 234, 0.15)',
              border: '1px solid rgba(102, 126, 234, 0.3)',
              borderRadius: '12px',
              padding: '8px 14px',
              color: 'white',
              cursor: 'pointer',
              transition: 'all 0.2s',
              minHeight: '44px',
            }}
          >
            <div style={{
              width: '32px',
              height: '32px',
              minWidth: '32px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea, #00d4ff)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.85rem',
              fontWeight: '700',
            }}>
              {initials}
            </div>
            <span className="header-search-label" style={{ fontSize: '0.85rem', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName}
            </span>
            <svg className="header-search-label" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6, transform: showUserMenu ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {showUserMenu && (
            <div
              role="menu"
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                minWidth: '200px',
                maxWidth: 'calc(100vw - 40px)',
                background: 'rgba(10, 14, 26, 0.98)',
                border: '1px solid rgba(102, 126, 234, 0.3)',
                borderRadius: '12px',
                padding: '8px',
                zIndex: 1000,
                boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
              }}
            >
              {!isDemo && user?.email && (
                <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '4px' }}>
                  <div style={{ fontSize: '0.85rem', color: 'white', fontWeight: '600' }}>{user.name || 'User'}</div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: '2px', wordBreak: 'break-all' }}>{user.email}</div>
                </div>
              )}
              {isDemo && (
                <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '4px' }}>
                  <div style={{ fontSize: '0.8rem', color: '#00d4ff' }}>Demo Mode</div>
                  <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>Viewing simulated data</div>
                </div>
              )}
              <button
                role="menuitem"
                onClick={() => { setShowUserMenu(false); onLogout?.(); }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#ef4444',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'background 0.15s',
                  minHeight: '44px',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                {isDemo ? 'Exit Demo' : 'Sign Out'}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
